import React, { useState } from 'react';
import styled from 'styled-components';
import { Heading, Text, Button } from '../styles/components';
import assessmentPlaceholder from '../assets/assessment-placeholder.png';

const VERTICAL_IMAGES = {
  'Robotics / Physical AI': '/assets/robotics.png',
  'Advanced Materials': '/assets/materials.png',
  'Biotech / Life Sciences': '/assets/biotech.png',
  'Energy & Climate': '/assets/energy.png',
  'Defense / Dual-Use': '/assets/defence.png',
  'Space': '/assets/space.png',
  'Aerospace': '/assets/aerospace.png',
  'Agriculture / Food Systems': '/assets/agri.png',
  'Industrial / Hardware / Manufacturing': '/assets/manufacturing.png',
  'Semiconductors / Compute Infrastructure': '/assets/semiconductors.png',
  'Quantum / Frontier Compute': '/assets/quantum.png',
  'Misc': '/assets/misc.png'
};

const DossierCard = styled.div`
  background-color: ${props => props.theme.colors.bone};
  border-radius: ${props => props.theme.radii.lg};
  box-shadow: 0 20px 50px rgba(0,0,0,0.3);
  padding: ${props => props.theme.spacing.xxl};
  width: 100%;
  border: 1px solid rgba(255,255,255,0.1);
  position: relative;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.lg};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.charcoal};
  padding-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.span`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-weight: ${props => props.theme.typography.heading.fontWeight};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textLight};
`;

const TabsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: 1rem;
  color: ${props => props.$active ? props.theme.colors.terracotta : props.theme.colors.textLight};
  border-bottom: 2px solid ${props => props.$active ? props.theme.colors.terracotta : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.colors.terracotta};
  }
`;

// --- Overview Components ---
const OverviewGrid = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.xl};

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
        grid-template-columns: 1fr;
    }
`;

const ImagePlaceholder = styled.div`
    width: 80%;
    max-width: 250px;
    align-self: center;
    aspect-ratio: 1;
    background-image: url(${props => props.src});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: ${props => props.theme.radii.md};
`;

const ListsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${props => props.theme.spacing.xl};
    margin-top: ${props => props.theme.spacing.lg};

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
        grid-template-columns: 1fr;
    }
`;


const StyledList = styled.ul`
    padding-left: ${props => props.theme.spacing.lg};
    margin: 0;
    
    li {
        margin-bottom: ${props => props.theme.spacing.sm};
        color: ${props => props.theme.colors.text};
    }
`;

// --- Strategy Components ---
const PillarsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.xxl};

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
        grid-template-columns: 1fr;
    }
`;

const PillarColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
`;

const PillarTitle = styled(Heading)`
    font-size: 1rem;
    border-bottom: 2px solid ${props => props.theme.colors.charcoal};
    padding-bottom: ${props => props.theme.spacing.xs};
    margin-bottom: ${props => props.theme.spacing.md};
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const GapCard = styled.div`
    background: white;
    border-radius: ${props => props.theme.radii.card};
    padding: ${props => props.theme.spacing.md};
    border-left: 4px solid ${props => props.$score >= 8 ? props.theme.colors.sage : props.$score >= 5 ? props.theme.colors.gold : props.theme.colors.terracotta};
    box-shadow: ${props => props.theme.shadows.sm};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${props => props.theme.shadows.md};
    }
`;

const GapHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing.xs};
`;

const GapTitle = styled.h4`
    font-size: 1rem;
    font-weight: bold;
    margin: 0;
    text-transform: capitalize;
`;

const GapScore = styled.span`
    font-weight: bold;
    color: ${props => props.theme.colors.charcoal};
`;

const GapReason = styled.p`
    font-size: 0.875rem;
    color: ${props => props.theme.colors.text};
    line-height: 1.4;
    margin: 0;
    
    ${props => !props.$expanded && `
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    `}
`;

const ExpandedContent = styled.div`
    margin-top: ${props => props.theme.spacing.sm};
    padding-top: ${props => props.theme.spacing.sm};
    border-top: 1px solid ${props => props.theme.colors.border};
    font-size: 0.85rem;
    color: ${props => props.theme.colors.textLight};
`;

// --- Question Components ---
const QuestionItem = styled.li`
    padding: ${props => props.theme.spacing.sm} 0;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    display: flex;
    align-items: center; /* Center badge vertically in row */
    gap: ${props => props.theme.spacing.md};
    
    &:last-child {
        border-bottom: none;
    }
`;

const ImportanceBadge = styled.span`
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    padding: 2px 6px;
    border-radius: 4px;
    color: white;
    background-color: ${props => {
    switch (props.$level) {
      case 'Critical': return props.theme.colors.terracotta;
      case 'High': return props.theme.colors.gold;
      default: return props.theme.colors.sage;
    }
  }};
    min-width: 60px;
    text-align: center;
    margin-top: 2px;
`;

const ConfidenceScore = styled.div`
    display: inline-block;
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    background-color: ${props => props.theme.colors.charcoal};
    color: ${props => props.theme.colors.bone};
    border-radius: ${props => props.theme.radii.sm};
    font-size: 0.875rem;
    font-weight: bold;
    margin-bottom: ${props => props.theme.spacing.lg};
`;

const PILLAR_MAPPING = [
  { title: "Desirability", items: ['problem', 'market', 'whyNow', 'vision'] },
  { title: "Feasibility", items: ['solution', 'defensibility', 'proof', 'roadmap'] },
  { title: "Viability", items: ['team', 'gtm', 'competition', 'financials'] }
];

const getVerticalImage = (vertical) => {
  if (!vertical) return assessmentPlaceholder;

  // Check exact match first
  if (VERTICAL_IMAGES[vertical]) return VERTICAL_IMAGES[vertical];

  const v = vertical.toLowerCase();

  if (v.includes('robotics')) return VERTICAL_IMAGES['Robotics / Physical AI'];
  if (v.includes('materials')) return VERTICAL_IMAGES['Advanced Materials'];
  if (v.includes('biotech')) return VERTICAL_IMAGES['Biotech / Life Sciences'];
  if (v.includes('energy') || v.includes('climate')) return VERTICAL_IMAGES['Energy & Climate'];
  if (v.includes('defense') || v.includes('defence')) return VERTICAL_IMAGES['Defense / Dual-Use'];
  if (v.includes('space')) return VERTICAL_IMAGES['Space'];
  if (v.includes('aerospace')) return VERTICAL_IMAGES['Aerospace'];
  if (v.includes('agri') || v.includes('food')) return VERTICAL_IMAGES['Agriculture / Food Systems'];
  if (v.includes('manufacturing') || v.includes('industrial') || v.includes('hardware')) return VERTICAL_IMAGES['Industrial / Hardware / Manufacturing'];
  if (v.includes('semiconductor') || v.includes('infrastructure')) return VERTICAL_IMAGES['Semiconductors / Compute Infrastructure'];
  if (v.includes('quantum') || v.includes('frontier')) return VERTICAL_IMAGES['Quantum / Frontier Compute'];

  return VERTICAL_IMAGES['Misc'] || assessmentPlaceholder;
};

const AssessmentResults = ({ results, email }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedCard, setExpandedCard] = useState(null);

  if (!results) return null;

  const renderQuestion = (q) => {
    const isObject = typeof q === 'object';
    const text = isObject ? q.question : q;
    const importance = isObject ? q.importance : 'Medium';

    return (
      <QuestionItem key={text}>
        <ImportanceBadge $level={importance}>{importance}</ImportanceBadge>
        <Text>{text}</Text>
      </QuestionItem>
    );
  };

  const gapMap = results.gap_map || {};

  // Helper to find key case-insensitively (e.g. 'Team' vs 'team' vs 'TEAM')
  const getGapItem = (targetKey) => {
    const keys = Object.keys(gapMap);
    const exact = gapMap[targetKey];
    if (exact) return exact;

    const lowerTarget = targetKey.toLowerCase();
    const match = keys.find(k => k.toLowerCase() === lowerTarget || k.toLowerCase().replace(/\s/g, '') === lowerTarget);
    return match ? gapMap[match] : null;
  };

  const getPillarScore = (items) => {
    return items.reduce((acc, key) => {
      const item = getGapItem(key);
      return acc + (item ? item.score : 0);
    }, 0);
  };

  return (
    <DossierCard>
      <HeaderRow>
        <Label>Nicely Put // Assessment Report</Label>
        <Label>{new Date().toLocaleDateString()}</Label>
      </HeaderRow>

      <TabsContainer>
        <TabButton
          $active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </TabButton>
        <TabButton
          $active={activeTab === 'strategy'}
          onClick={() => setActiveTab('strategy')}
        >
          Full Strategy Analysis
        </TabButton>
        <TabButton
          $active={activeTab === 'questions'}
          onClick={() => setActiveTab('questions')}
        >
          Key Investor Questions
        </TabButton>
      </TabsContainer>

      {/* --- OVERVIEW TAB --- */}
      {activeTab === 'overview' && (
        <>
          <Label style={{ display: 'block', marginBottom: '0.5rem' }}>The Investment Case</Label>
          <ConfidenceScore>Confidence: {results.confidence_score || 85}%</ConfidenceScore>

          <OverviewGrid>
            <div>
              <Text style={{ fontSize: '1.25rem', lineHeight: '1.4', marginBottom: '1.5rem' }}>{results.investment_case}</Text>
              <Text size="small" color="textLight" style={{ fontStyle: 'italic', borderLeft: '3px solid #ccc', paddingLeft: '12px' }}>
                This is what the AI analysis suggests your investment case is <strong>based on the slides in this deck</strong>. If it differs from your actual case, the deck may be misleading.
              </Text>
            </div>
            <div>
              <ImagePlaceholder
                src={
                  getVerticalImage(results.vertical_detected)
                }
              />
            </div>
          </OverviewGrid>

          <ListsGrid>
            <div>
              <Heading as="h4" size="h5" color="sage" $mb="1rem">Supporting Elements</Heading>
              <StyledList>
                {results.supporting_elements?.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </StyledList>
            </div>
            <div>
              <Heading as="h4" size="h5" color="terracotta" $mb="1rem">Risks & Weaknesses</Heading>
              <StyledList>
                {results.weaknesses?.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </StyledList>
            </div>
          </ListsGrid>

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Button onClick={() => setActiveTab('strategy')} $secondary>
              View Detailed Analysis
            </Button>
          </div>
        </>
      )}

      {/* --- STRATEGY TAB --- */}
      {activeTab === 'strategy' && (
        <>
          <Heading as="h3" size="h5" color="charcoal" $mb="1rem" style={{ textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1rem' }}>Strategic Gap Map</Heading>
          <Text size="small" color="textLight" style={{ marginBottom: '1.5rem', fontStyle: 'italic' }}>
            This map highlights where your current deck is strong (green) versus where it exposes risks (red). A 'fundable' deck typically scores 8+ across at least 9 categories.
          </Text>
          <PillarsGrid>
            {PILLAR_MAPPING.map((pillar) => (
              <PillarColumn key={pillar.title}>
                <PillarTitle as="h4">
                  <span>{pillar.title}</span>
                  <span style={{ fontSize: '0.9em', opacity: 0.7 }}>{getPillarScore(pillar.items)}/40</span>
                </PillarTitle>
                {pillar.items.map(key => {
                  const item = getGapItem(key) || { score: 0, reason: "No data available" };
                  const isExpanded = expandedCard === key;

                  return (
                    <GapCard
                      key={key}
                      $score={item.score}
                      onClick={() => setExpandedCard(isExpanded ? null : key)}
                    >
                      <GapHeader>
                        <GapTitle>{key.replace(/([A-Z])/g, ' $1').trim()}</GapTitle>
                        <GapScore>{item.score}/10</GapScore>
                      </GapHeader>
                      <GapReason $expanded={isExpanded}>
                        {item.reason}
                      </GapReason>
                      {isExpanded && (
                        <ExpandedContent>
                          Click to collapse
                        </ExpandedContent>
                      )}
                    </GapCard>
                  );
                })}
              </PillarColumn>
            ))}
          </PillarsGrid>
        </>
      )}

      {/* --- QUESTIONS TAB --- */}
      {activeTab === 'questions' && (
        <>
          <Heading as="h3" size="h5" color="charcoal" $mb="1rem" style={{ textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1rem' }}>
            Key Investor Questions
          </Heading>
          <div style={{ background: 'white', padding: '2rem', borderRadius: '8px' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {results.investor_questions?.map(renderQuestion)}
              {(!results.investor_questions && results.questions) && results.questions.map(renderQuestion)}
            </ul>
          </div>
        </>
      )}

      {/* --- FOOTER (Always Visible) --- */}
      <div style={{ marginTop: '3rem', textAlign: 'center', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '2rem' }}>
        <Text $mb="1rem" size="small" color="textLight">
          Full specific details for your vertical ({results.vertical_detected || 'General'}) sent to {email}
        </Text>
        <Button
          onClick={() => window.open('https://calendly.com/ellierofe/intro-chat', '_blank')}
          $variant="primary"
        >
          Discuss Strategy with Ellie
        </Button>
      </div>
    </DossierCard>
  );
};

export default AssessmentResults;
